import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';
import styled from 'styled-components';

import { useWindowWidth } from '@treams/treams-hooks';
import { EvaluationFormFragment } from '@treams/treams-network';
import { Modal, Paragraph, Popover, Sidebar, Subheading, tokens } from '@treams/treams-ui';

import {
	SelectConversationTypeCard as InfoCard,
	SelectConversationTypeItem as Item,
	SelectConversationTypeCardNoSelection as NoSelection,
} from '.';
import { SelectConversationAccordionGroup } from './SelectConversationAccordionGroup';

type Props = {
	visible: boolean;
	forms: EvaluationFormFragment[];
	selected?: EvaluationFormFragment | undefined;
	onClose: () => void;
	onSave: (form: EvaluationFormFragment) => void;
};

export const SelectConversationTypeModal = ({
	selected,
	visible,
	forms,
	onClose,
	onSave,
}: Props) => {
	const { t } = useTranslation('', {
		keyPrefix: 'evaluations.create.modal.form.selectConversationType.modal',
	});

	const selectionButtons = forms.map((form) => (
		<Item key={form.id} form={form} onClick={onSave} selected={selected} />
	));

	const handleSave = () => {
		if (selected) {
			onSave(selected);
			onClose();
		}
	};

	const isMobile = useWindowWidth(640);

	return (
		<Popover backdrop center visible={visible}>
			<Modal width="1000px">
				<Modal.Header title={t('header')} />
				<StyledContents p={0}>
					{isMobile ? (
						<SelectConversationAccordionGroup forms={forms} selected={selected} onSave={onSave} />
					) : (
						<StyledFlex>
							<StyledSideBar p={1} pt={0}>
								<Subheading p={2} pt={4}>
									<Paragraph>{t('sidebar.title')}</Paragraph>
								</Subheading>
								{selectionButtons}
							</StyledSideBar>
							<StyledContent>
								{selected ? <InfoCard {...selected} /> : <NoSelection />}
							</StyledContent>
						</StyledFlex>
					)}
				</StyledContents>
				<Modal.FormFooter onCancel={onClose} onSave={handleSave} saveTitle={t('footer.save')} />
			</Modal>
		</Popover>
	);
};

const StyledContents = styled(Modal.Contents)`
	@media (min-width: 640px) {
		overflow: hidden;
	}
`;

const StyledFlex = styled(Flex)`
	height: 100%;
	min-height: 0;
`;

const StyledSideBar = styled(Sidebar)`
	width: 25%;
	flex-shrink: 0;
	overflow-y: auto;
	scrollbar-gutter: stable;
	border-right: 1px solid ${tokens.border.subtle};
`;

const StyledContent = styled(Flex)`
	flex: 1;
	min-width: 0;
	overflow-y: auto;
	scrollbar-gutter: stable;
`;
